import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  createPaymentProvider,
  deletePaymentProvider,
  listPaymentProviders,
  testPaymentProviderConnection,
  updatePaymentProvider,
} from '../../api/paymentSettingsApi';
import { settingsKeys, usePaymentSettings, useUpdatePaymentSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { PaymentCaptureMode, PaymentMethod } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import { PAYMENT_CAPTURE_MODE_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../options';

/** Payment methods, providers, fiscalisation and credentials. */
export function PaymentSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('payments');
  const query = usePaymentSettings();
  const update = useUpdatePaymentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('paymentSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('paymentSettings.title')}
      subtitle={t('paymentSettings.subtitle')}
      icon={<CreditCardRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('paymentSettings.methodsSection.title')} icon={<CreditCardRoundedIcon />}>
              <MultiToggleField
                label={t('paymentSettings.methodsSection.enabledMethodsLabel')}
                value={draft.enabledMethods}
                options={PAYMENT_METHOD_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('enabledMethods', value as PaymentMethod[])}
                disabled={disabled}
              />
              <SelectField
                label={t('paymentSettings.methodsSection.captureModeLabel')}
                value={draft.captureMode}
                options={PAYMENT_CAPTURE_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('captureMode', value as PaymentCaptureMode)}
                disabled={disabled}
              />
              <SwitchField
                label={t('paymentSettings.methodsSection.refundsLabel')}
                description={t('paymentSettings.methodsSection.refundsDescription')}
                checked={draft.refundsEnabled}
                onChange={(value) => form.setField('refundsEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('paymentSettings.fiscalisationSection.title')} icon={<ReceiptRoundedIcon />}>
              <TextInputField
                label={t('paymentSettings.fiscalisationSection.fiscalProviderLabel')}
                description={t('paymentSettings.fiscalisationSection.fiscalProviderDescription')}
                value={draft.fiscalProvider ?? ''}
                onChange={(value) => form.setField('fiscalProvider', value || null)}
                disabled={disabled}
              />
              <ChipListField
                label={t('paymentSettings.fiscalisationSection.restrictionsLabel')}
                value={draft.restrictions}
                onChange={(value) => form.setField('restrictions', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title={t('paymentSettings.providersTitle')}
              description={t('paymentSettings.providersDescription')}
              icon={<CreditCardRoundedIcon />}
              listKey={settingsKeys.paymentProviders}
              api={{
                list: listPaymentProviders,
                create: createPaymentProvider,
                update: updatePaymentProvider,
                remove: deletePaymentProvider,
                test: testPaymentProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint={t('paymentSettings.providersCredentialHint')}
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
